// This generates the book HTML

import async from "async";
import * as _ from "underscore";
import _XRegExp from "xregexp";

import spannify from "./spannify.js";

const XRegExp = (() => _XRegExp.XRegExp ?? _XRegExp)();

/**
 * If this is updated, then spannify.js needs to change.
 *
 * Before all versions before 0.6, books will be regenerated using the 0.6 algo, so as not to
 * mess up the positions (in UserBook and YouTube audio sync and Reader Review).
 * For version 0.7, books will be regenerated using the 0.7 algo (includes punctuation).
 */
export const htmlVersion = 0.7;

const htmlCharacters = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;", //,
  //    '"': '&quot;',
  //    "'": '&#39;'
};

/**
 * This is added as the first top-level section name if the text happens to start with a
 * second-level section header.
 */
export const dummyFirstSectionName = "Entire book";

export const escapeHTML = (input) =>
  input.replace(/[&<>]/g, (c) => htmlCharacters[c]);

export const categories = [
  "Fiction",
  "Non-Fiction",
  "Song",
  "Conversation",
  "Other",
];

export const textToHtml = (plainText, options) => {
  let html = escapeHTML(plainText);

  html = html
    //.replace(/!\[(.+?)\]\((.+?)\)/g, '</p><img src="$2" alt="$1" /><p>')

    .replace(/\u00AD/g, "")

    // consolidate adjacent italic strings
    .replace(/([^_]_[^_\r\n]+)__([^_\r\n]+_[^_])/g, "$1$2")

    .replace(/(^|\r|\n)(.+?)(\r|\n)=+(\r|\n|$)/g, "</p><h1>$2</h1><p>")
    .replace(/(^|\r|\n)# (.+?)(\r|\n|$)/g, "</p><h1>$2</h1><p>")
    .replace(/(^|\r|\n)(.+?)(\r|\n)-+(\r|\n|$)/g, "</p><h2>$2</h2><p>")
    .replace(/(^|\r|\n)## (.+?)(\r|\n|$)/g, "</p><h2>$2</h2><p>")
    .replace(/(^|\r|\n)### (.+?)(\r|\n|$)/g, "</p><h3>$2</h3><p>")

    .replace(/__([^_\r\n]+?)__/g, "<strong>$1</strong>")
    .replace(/\*\*([^*\r\n]+?)\*\*/g, "<strong>$1</strong>")

    .replace(/_([^_\r\n]+?)_/g, "<i>$1</i>")
    .replace(/\*([^*\r\n]+?)\*/g, "<i>$1</i>")

    // Retain superscript HTML which has now been escaped
    .replace(/\^([^<\r\n]+?)\^/g, "<sup>$1</sup>")

    .replace(/(\r\n|\n\r|\n|\r){2,}/g, "</p><p>")

    // Remove empty newlines from start of paragraphs
    .replace(/<p>(\s+)?/g, "<p>")

    // Unescape exclamation marks:
    .replace(/\\!/g, "!");

  if (options.useEveryNewLine) {
    html = html.replace(/\r\n|\n\r|\n|\r/g, "<br>");
  } else {
    html = html.replace(/\r\n|\n\r|\n|\r/g, " ");
  }
  html = "<p>" + html + "</p>";

  // Remove empty paragraphs from start and end of text
  if (html.startsWith("<p></p>")) {
    html = html.substring(7);
  }
  if (html.endsWith("<p></p>")) {
    html = html.substring(0, html.length - 7);
  }

  return html;
};

const htmlTags = /<\/?[a-z]+[^<]*?>/g;
const stripHtmlTags = (input) => input.replace(htmlTags, "");

const createTableOfContents = (html) => {
  // create table of contents from <h1> and <h2> tags
  const header1Regexp = /<h1>(.*?)<\/h1>/g;
  const header2Regexp = /<h2>(.*?)<\/h2>/g;
  const spanRegexp = /<span data-p=([0-9]+)>/;
  let match;
  const sections = [];
  let firstSpan;
  let spanIndex;

  // Find <h1> tags
  while ((match = header1Regexp.exec(html)) !== null) {
    // find first span...
    firstSpan = spanRegexp.exec(match[1]);
    if (firstSpan) {
      spanIndex = parseInt(firstSpan[1], 10);
      sections.push({
        name: stripHtmlTags(match[1]),
        start: spanIndex,
        children: [],
      });
    }
  }
  let currentSection = 0;
  // Find <h2> tags
  while ((match = header2Regexp.exec(html)) !== null) {
    // find first span...
    firstSpan = spanRegexp.exec(match[1]);
    if (firstSpan) {
      spanIndex = parseInt(firstSpan[1], 10);

      while (
        sections[currentSection + 1] &&
        spanIndex > sections[currentSection + 1].start
      ) {
        currentSection++;
      }
      if (currentSection === 0 && sections.length === 0) {
        // TODO: always add this and give it the book title!!
        sections.push({
          name: dummyFirstSectionName,
          start: 0,
          children: [],
        });
      }
      sections[currentSection].children.push({
        name: stripHtmlTags(match[1]),
        start: spanIndex,
      });
    }
  }
  return sections;
};

export const needToGenerateHtml = (book) => {
  return !book.generatedVersion || book.generatedVersion < 0.6;
};

// TODO: figure out why XRegExp doesn't load in nodejs,
//       something to do with it being a shim?
//
// The callback arguments are (error, book, changed)
/**
 * @param {{
 *   callback: (error: any, book: any, changed: boolean, html?: string) => void;
 *   dataStore: any;
 *   forceRegenerate?: boolean;
 *   wordsPerChunk?: number;
 *   language?: string;
 *   XRegExp?: any;
 *   ignoreStrong?: boolean;
 *   includePunctuation?: boolean;
 *   startPosition?: number;
 *   onEachSpan?: (position: number, offset: number, cumulativeOffset: number) => void;
 * }} options
 */
export const generateHtml = (book, plainText, options) => {
  _.defaults(options, {
    wordsPerChunk: 1024,
  });

  /** @type {number} */
  let thisHtmlVersion;

  // Books from 2024-06-07 18:30:00 onwards which have generatedVersion=0.6 should
  // actually have generatedVersion=0.7 since they used the newer algo.
  if (
    book.generatedVersion === 0.6 &&
    book._id.getTimestamp().getTime() >= new Date(2024, 5, 7, 18, 30).getTime()
  ) {
    book.generatedVersion = 0.7;
  }

  if (!options.forceRegenerate && !needToGenerateHtml(book)) {
    options.callback(null, book, false);
    return;
  }

  // Let's (re)generate the HTML
  thisHtmlVersion =
    book.generatedVersion === undefined
      ? htmlVersion
      : book.generatedVersion >= 0.7
        ? book.generatedVersion
        : 0.6;
  delete book.htmlChunks;
  delete book.totalWords;
  delete book.generatedText1;
  delete book.generatedText2;

  if (typeof book.useEveryNewLine === "string") {
    book.useEveryNewLine = JSON.parse(book.useEveryNewLine);
  }

  const htmlFormattingOptions = {
    htmlMarkup: book.htmlMarkup,
    useEveryNewLine: book.useEveryNewLine,
  };
  const html = textToHtml(plainText, htmlFormattingOptions);

  let thisChunkFirstPosition = 0;
  let thisChunkFirstCharacter = 0;
  const chunks = [
    {
      firstCharacter: thisChunkFirstCharacter,
      firstPosition: thisChunkFirstPosition,
      positionIndexes: [],
    },
  ];
  const chunkSplitPoints = [0];

  options.onEachSpan = (position, offset, cumulativeOffset) => {
    const thisCharacterIndex = offset + cumulativeOffset;
    const chunkIndex = Math.floor(position / options.wordsPerChunk);

    if (chunks.length - 1 < chunkIndex) {
      chunks[chunks.length - 1].lastPosition = position - 1;
      thisChunkFirstPosition = position;
      thisChunkFirstCharacter = thisCharacterIndex;
      chunks.push({
        firstCharacter: thisChunkFirstCharacter,
        firstPosition: thisChunkFirstPosition,
        positionIndexes: [],
      });
      chunkSplitPoints.push(thisCharacterIndex);
    }

    const relativePosition = position - thisChunkFirstPosition;
    if (relativePosition > 0 && relativePosition % 8 === 0) {
      chunks[chunkIndex].positionIndexes[Math.floor(relativePosition / 8) - 1] =
        thisCharacterIndex - thisChunkFirstCharacter;
    }
  };
  options.language = book.language;
  options.XRegExp = XRegExp;

  if (book.htmlMarkup) {
    options.ignoreStrong = true;
  }

  options.includePunctuation = thisHtmlVersion >= 0.7;

  const spannified = spannify(html, options);

  if (book.imageURL) {
    spannified.html = '<img src="' + book.imageURL + '"/>' + spannified.html;
  }

  book.totalWords = spannified.totalSpans;
  chunks[chunks.length - 1].lastPosition = book.totalWords - 1;
  chunkSplitPoints.push(spannified.html.length);

  book.sections = createTableOfContents(spannified.html);

  // split the html into chunks
  async.eachLimit(
    chunks.map((chunk, index) => ({ chunk, index })),
    10,
    async ({ chunk, index }, eachCallback) => {
      const chunkHtml = spannified.html.substring(
        chunkSplitPoints[index],
        chunkSplitPoints[index + 1]
      );
      const sha1 = await options.dataStore.addChunk(chunkHtml);
      chunk.sha1 = sha1;
      eachCallback();
    },
    (err) => {
      if (err) {
        console.error(err);
      }
      book.htmlChunks = chunks;
      book.generatedVersion = thisHtmlVersion;
      options.callback(null, book, true, spannified.html);
    }
  );
};

/**
 * @param {string} html
 *
 * @returns {string[]}
 */
export const htmlToWordList = (html) => {
  const wordRegexp = new RegExp("<span data-p=([0-9]+)?>(.*?)</span>", "g");
  const words = [];
  let match;
  while ((match = wordRegexp.exec(html)) !== null) {
    console.assert(words.length === parseInt(match[1], 10));
    words.push(match[2]);
  }
  return words;
};
